import { type Message as AgUiMessage } from '@ag-ui/client'
import toCamelCase from 'camelcase-keys'
import { ApiClientError, baseClient } from './fetch'

// Copilot BFF routes (`/api/copilot/*`) use cookie auth on the Next.js origin.
// Knowledge-source helpers go through the shared fetch client; other Copilot
// surfaces still use native fetch until migrated the same way.

export const paths = {
  chats: '/api/copilot/chats',
  userInstructions: '/api/copilot/instructions/user',
  workspaceInstructions: '/api/copilot/instructions/workspace',
  knowledgeSources: '/api/copilot/knowledge-sources',
  skills: '/api/copilot/skills',
  teamworkSpaces: '/api/copilot/teamwork/spaces'
}

const readResponseError = async (response: Response): Promise<{ message?: string; code?: string }> => {
  const contentType = response.headers.get('content-type')
  const isJson = contentType?.includes('application/json') === true
  if (!isJson) {
    return {}
  }

  try {
    const json: unknown = await response.json()
    if (json == null || typeof json !== 'object' || !('error' in json)) {
      return {}
    }

    const error = (json as { error?: unknown }).error
    if (typeof error === 'string') {
      return { message: error }
    }
    if (error != null && typeof error === 'object') {
      const record = error as { message?: unknown; code?: unknown }
      return {
        message: typeof record.message === 'string' ? record.message : undefined,
        code: typeof record.code === 'string' ? record.code : undefined
      }
    }
  } catch {
    return {}
  }

  return {}
}

export const apiClientErrorFromResponse = async (
  response: Response,
  fallbackMessage: string,
  endpoint?: string
): Promise<ApiClientError> => {
  const parsed = await readResponseError(response)
  return new ApiClientError({
    message: parsed.message ?? fallbackMessage,
    code: parsed.code,
    status_code: response.status,
    endpoint
  })
}

/**
 * Same-origin BFF client for Knowledge Sources.
 * Keeps camelCase request bodies (BFF Zod schemas) and unwraps Alvie `{ data }` envelopes.
 */
const knowledgeSourcesClient = baseClient({ headers: { 'content-type': 'application/json' } }, (args) => ({
  path: args.path,
  headers: { 'content-type': 'application/json' },
  data: args.data != null ? { ...args.data } : null,
  responseHandler: {
    json: <T>(response: T): T => {
      if (args.rawResponse === true) {
        return toCamelCase(response as Record<string, unknown>, { deep: true }) as T
      }
      const camel = toCamelCase(response as Record<string, unknown>, { deep: true }) as Record<string, unknown>
      if (camel != null && typeof camel === 'object' && 'data' in camel) {
        return camel.data as T
      }
      return camel as T
    },
    text: <T>(text: T): T => text,
    blob: <T>(blob: T): T => blob
  }
}))

export type HydratedAgUiMessage = AgUiMessage & {
  createdAt?: string
  csvAttachMode?: string
  attachCsvFile?: { gcs_uri: string; filename: string }
}

export interface CopilotChat {
  id: string
  title: string
  adk_session_id: string
  /** Agent the chat runs on; null for chats created before Flow recorded it. */
  agent_name?: string | null
  created_at: string
  updated_at: string
}

interface CopilotChatsResponse {
  data: CopilotChat[]
  meta: { total: number; page: number; perPage: number }
}

export interface CopilotChatDetail {
  id: string
  title: string
  adkSessionId: string
  agentName?: string | null
  createdAt: string
  updatedAt: string
  messages: HydratedAgUiMessage[]
}

export interface CopilotInstructionsDocument {
  docType: string
  content: string
  lastUpdatedAt: string | null
  updatedBy: string | null
  characterLimit: number | null
  originalFilename?: string | null
  hasFile?: boolean
}

export interface UpdateUserInstructionsPayload {
  content: string
}

export interface UpdateWorkspaceInstructionsPayload {
  content: string
  lastUpdatedAt: string | null
}

// --- Knowledge sources ---

export type CorpusType = 'generic' | 'client'
export type CorpusSyncStatus = 'pending' | 'in_progress' | 'completed' | 'failed'
export type DocumentSyncStatus = 'pending' | 'synced' | 'failed' | 'deleted'
export type KnowledgeSourceType = 'google_drive' | 'intercom' | 'teamwork_spaces'

export interface FolderEntry {
  id: string
  recursive?: boolean
}

export interface CollectionEntry {
  id: string
  recursive?: boolean
}

export interface SpaceEntry {
  id: string
}

export type KnowledgeSourceFilterOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'greater_than'
  | 'less_than'
  | 'is_empty'
  | 'is_not_empty'

export type KnowledgeSourceFilterLogic = 'and' | 'or'

export interface KnowledgeSourceFilterCondition {
  field: string
  operator: KnowledgeSourceFilterOperator
  value: string | number | boolean | null
}

export interface KnowledgeSourceFilterGroup {
  logic: KnowledgeSourceFilterLogic
  conditions: KnowledgeSourceFilterCondition[]
  groups: KnowledgeSourceFilterGroup[]
}

export interface KnowledgeSourceConfig {
  id?: string
  type: KnowledgeSourceType
  folders?: FolderEntry[]
  mimeTypeFilter?: string[] | null
  namePattern?: string | null
  collections?: CollectionEntry[]
  spaces?: SpaceEntry[]
  filters?: KnowledgeSourceFilterGroup | null
}

export interface KnowledgeUserSummary {
  id: string
  email: string
  avatarUrl?: string | null
}

export interface KnowledgeTag {
  id: string
  name: string
  description: string | null
  createdAt: string
  modifiedAt: string | null
}

export interface CorpusResponse {
  id: string
  name: string
  description: string | null
  createdBy: string | null
  owner: KnowledgeUserSummary | null
  collaborators: KnowledgeUserSummary[]
  corpusType: CorpusType
  vertexCorpusName: string | null
  vertexCorpusId: string | null
  clientExternalId: number | null
  sourceConfigs: KnowledgeSourceConfig[]
  availableToInternal: boolean
  availableToExternal: boolean
  syncStatus: CorpusSyncStatus
  lastSyncAt: string | null
  lastSyncError: string | null
  tags: KnowledgeTag[]
  documentCount: number
  failedDocumentCount: number
  createdAt: string
  modifiedAt: string | null
}

/** Flow Alvie list envelope (Alvie API design guide). */
export interface AlvieListResponse<T> {
  data: T[]
  statusCode?: number
  nextCursor?: string
  previousCursor?: string
  limit?: number
}

export interface CreateCorpusPayload {
  name: string
  description?: string | null
  corpusType?: 'client'
  clientExternalId?: number
  sourceConfigs?: KnowledgeSourceConfig[]
  availableToInternal?: boolean
  availableToExternal?: boolean
}

export interface UpdateCorpusPayload {
  name?: string
  description?: string | null
  clientExternalId?: number
  sourceConfigs?: KnowledgeSourceConfig[]
  removeDocumentsFromSourceId?: string
  availableToInternal?: boolean
  availableToExternal?: boolean
}

export interface CorpusDeleteResponse {
  corpusDeleted: boolean
  documentsDeleted: number
}

export interface DocumentBase {
  id: string
  sourceId: string
  sourceType: string
  sourceUrl: string | null
  name: string
  mimeType: string | null
  sizeBytes: number | null
  contentHash: string | null
  createdAt: string
  modifiedAt: string | null
}

export interface CorpusDocumentInfo {
  corpusId: string
  isManual: boolean
  syncStatus: DocumentSyncStatus
  lastSyncAt: string | null
  lastCheckedAt: string | null
  syncError: string | null
  vertexFileName: string | null
  vertexFileId: string | null
}

export interface DocumentInCorpus {
  document: DocumentBase
  corpusInfo: CorpusDocumentInfo
}

export interface DocumentContentResponse {
  documentId: string
  sourceType: string
  mimeType: string | null
  content: string
}

export interface TeamworkSpace {
  code: string
  name: string
}

export interface KnowledgeCorpusEntityState {
  id: string
  type: 'knowledge_corpus'
  name: string
  data: CorpusResponse
}

export const listCopilotChats = async (clientId: number): Promise<CopilotChat[]> => {
  const res = await fetch(`${paths.chats}?clientId=${encodeURIComponent(clientId)}`)
  if (!res.ok) {
    throw await apiClientErrorFromResponse(res, 'Failed to fetch copilot chats')
  }
  const json: CopilotChatsResponse = await res.json()
  return json.data
}

export const getCopilotChat = async (clientId: number, chatId: string): Promise<CopilotChatDetail> => {
  const res = await fetch(`${paths.chats}/${encodeURIComponent(chatId)}?clientId=${encodeURIComponent(clientId)}`)
  if (!res.ok) {
    throw await apiClientErrorFromResponse(res, 'Failed to fetch copilot chat')
  }
  return await res.json()
}

export const getUserInstructions = async (clientId: number): Promise<CopilotInstructionsDocument> => {
  const res = await fetch(`${paths.userInstructions}?clientId=${encodeURIComponent(clientId)}`)
  if (!res.ok) {
    throw await apiClientErrorFromResponse(res, 'Failed to load or save copilot instructions')
  }
  return await res.json()
}

export const updateUserInstructions = async (
  clientId: number,
  payload: UpdateUserInstructionsPayload
): Promise<CopilotInstructionsDocument> => {
  const res = await fetch(`${paths.userInstructions}?clientId=${encodeURIComponent(clientId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })
  if (!res.ok) {
    throw await apiClientErrorFromResponse(res, 'Failed to load or save copilot instructions')
  }
  return await res.json()
}

export const getWorkspaceInstructions = async (clientId: number): Promise<CopilotInstructionsDocument> => {
  const res = await fetch(`${paths.workspaceInstructions}?clientId=${encodeURIComponent(clientId)}`)
  if (!res.ok) {
    throw await apiClientErrorFromResponse(res, 'Failed to load or save copilot instructions')
  }
  return await res.json()
}

export const updateWorkspaceInstructions = async (
  clientId: number,
  payload: UpdateWorkspaceInstructionsPayload
): Promise<CopilotInstructionsDocument> => {
  const res = await fetch(`${paths.workspaceInstructions}?clientId=${encodeURIComponent(clientId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })
  if (!res.ok) {
    throw await apiClientErrorFromResponse(res, 'Failed to load or save copilot instructions')
  }
  return await res.json()
}

const knowledgeSourcesQuery = (clientId: number, extra?: Record<string, string | undefined>): string => {
  const search = new URLSearchParams({ clientId: clientId.toString() })
  if (extra != null) {
    for (const [key, value] of Object.entries(extra)) {
      if (value != null && value !== '') {
        search.set(key, value)
      }
    }
  }
  return search.toString()
}

export const listKnowledgeSources = async (
  clientId: number,
  params?: { limit?: number; page?: number; perPage?: number }
): Promise<CorpusResponse[]> => {
  const pageSize = params?.perPage ?? params?.limit
  return await knowledgeSourcesClient.get<CorpusResponse[]>({
    path: `${paths.knowledgeSources}?${knowledgeSourcesQuery(clientId, {
      perPage: pageSize != null ? String(pageSize) : undefined,
      page: params?.page != null ? String(params.page) : undefined
    })}`
  })
}

export const listCompanyWideKnowledgeSources = async (
  clientId: number,
  params?: { limit?: number; page?: number; perPage?: number }
): Promise<CorpusResponse[]> => {
  const pageSize = params?.perPage ?? params?.limit
  return await knowledgeSourcesClient.get<CorpusResponse[]>({
    path: `${paths.knowledgeSources}/company-wide?${knowledgeSourcesQuery(clientId, {
      perPage: pageSize != null ? String(pageSize) : undefined,
      page: params?.page != null ? String(params.page) : undefined
    })}`
  })
}

export const getKnowledgeSource = async (clientId: number, corpusId: string): Promise<CorpusResponse> => {
  return await knowledgeSourcesClient.get<CorpusResponse>({
    path: `${paths.knowledgeSources}/${encodeURIComponent(corpusId)}?${knowledgeSourcesQuery(clientId)}`
  })
}

export const createKnowledgeSource = async (
  clientId: number,
  payload: CreateCorpusPayload
): Promise<CorpusResponse> => {
  return await knowledgeSourcesClient.post<CorpusResponse>({
    path: `${paths.knowledgeSources}?${knowledgeSourcesQuery(clientId)}`,
    data: { ...payload, clientExternalId: Number(clientId), corpusType: 'client' }
  })
}

export const updateKnowledgeSource = async (
  clientId: number,
  corpusId: string,
  payload: UpdateCorpusPayload
): Promise<CorpusResponse> => {
  return await knowledgeSourcesClient.patch<CorpusResponse>({
    path: `${paths.knowledgeSources}/${encodeURIComponent(corpusId)}?${knowledgeSourcesQuery(clientId)}`,
    data: { ...payload, clientExternalId: Number(clientId) }
  })
}

export const deleteKnowledgeSource = async (clientId: number, corpusId: string): Promise<CorpusDeleteResponse> => {
  return await knowledgeSourcesClient.del<CorpusDeleteResponse>({
    path: `${paths.knowledgeSources}/${encodeURIComponent(corpusId)}?${knowledgeSourcesQuery(clientId, {
      deleteOrphanedDocuments: 'true'
    })}`
  })
}

export const listKnowledgeSourceDocuments = async (
  clientId: number,
  corpusId: string,
  params?: { syncStatus?: DocumentSyncStatus; limit?: number; page?: number; perPage?: number }
): Promise<DocumentInCorpus[]> => {
  const pageSize = params?.perPage ?? params?.limit
  return await knowledgeSourcesClient.get<DocumentInCorpus[]>({
    path: `${paths.knowledgeSources}/${encodeURIComponent(corpusId)}/documents?${knowledgeSourcesQuery(clientId, {
      syncStatus: params?.syncStatus,
      perPage: pageSize != null ? String(pageSize) : undefined,
      page: params?.page != null ? String(params.page) : undefined
    })}`
  })
}

export const getKnowledgeSourceDocument = async (
  clientId: number,
  corpusId: string,
  documentId: string
): Promise<DocumentInCorpus> => {
  return await knowledgeSourcesClient.get<DocumentInCorpus>({
    path: `${paths.knowledgeSources}/${encodeURIComponent(corpusId)}/documents/${encodeURIComponent(
      documentId
    )}?${knowledgeSourcesQuery(clientId)}`
  })
}

export const getKnowledgeSourceDocumentContent = async (
  clientId: number,
  corpusId: string,
  documentId: string
): Promise<DocumentContentResponse> => {
  return await knowledgeSourcesClient.get<DocumentContentResponse>({
    path: `${paths.knowledgeSources}/${encodeURIComponent(corpusId)}/documents/${encodeURIComponent(
      documentId
    )}/content?${knowledgeSourcesQuery(clientId)}`
  })
}

export const uploadKnowledgeSourceDocuments = async (
  clientId: number,
  corpusId: string,
  files: File[]
): Promise<DocumentInCorpus[]> => {
  // Multipart must stay on native fetch — the shared JSON client sets application/json.
  const formData = new FormData()
  files.forEach((file) => {
    formData.append('files', file)
  })
  const res = await fetch(
    `${paths.knowledgeSources}/${encodeURIComponent(corpusId)}/documents/upload?${knowledgeSourcesQuery(clientId)}`,
    {
      method: 'POST',
      body: formData
    }
  )
  if (!res.ok) {
    throw await apiClientErrorFromResponse(res, 'Failed to upload documents')
  }
  return await res.json()
}

export const deleteKnowledgeSourceDocument = async (
  clientId: number,
  corpusId: string,
  documentId: string
): Promise<void> => {
  await knowledgeSourcesClient.del({
    path: `${paths.knowledgeSources}/${encodeURIComponent(corpusId)}/documents/${encodeURIComponent(
      documentId
    )}?${knowledgeSourcesQuery(clientId)}`
  })
}

export const refreshKnowledgeSource = async (
  clientId: number,
  corpusId: string,
  params?: { force?: boolean; fullRefresh?: boolean }
): Promise<CorpusResponse> => {
  return await knowledgeSourcesClient.post<CorpusResponse>({
    path: `${paths.knowledgeSources}/${encodeURIComponent(corpusId)}/refresh?${knowledgeSourcesQuery(clientId, {
      force: params?.force === true ? 'true' : undefined,
      fullRefresh: params?.fullRefresh === true ? 'true' : undefined
    })}`
  })
}

export const resyncFailedKnowledgeSourceDocuments = async (
  clientId: number,
  corpusId: string
): Promise<CorpusResponse> => {
  return await knowledgeSourcesClient.post<CorpusResponse>({
    path: `${paths.knowledgeSources}/${encodeURIComponent(corpusId)}/resync-failed?${knowledgeSourcesQuery(clientId)}`
  })
}

export const listTeamworkSpaces = async (clientId: number): Promise<TeamworkSpace[]> => {
  return await knowledgeSourcesClient.get<TeamworkSpace[]>({
    path: `${paths.teamworkSpaces}?${knowledgeSourcesQuery(clientId)}`
  })
}

export type FlowSkillScope = 'PRIVATE' | 'WORKSPACE' | 'ORGANIZATION'

export interface FlowSkillUserSummary {
  id?: string
  email?: string
  avatarUrl?: string | null
  alvieId?: number | null
}

export interface FlowSkillKnowledgeCorpus {
  name: string
  vertexCorpusId: string
  corpusType: 'generic' | 'client'
}

/** Shape returned by Flow `GET /api/v1/skills` (camelCased by the BFF). */
export interface FlowSkillListItem {
  id: number | string
  title: string
  name: string
  description?: string
  scope: FlowSkillScope
  scopeWorkspaceId?: number | null
  /** Raw Flow categories; may include values this UI does not know yet. */
  dataSourceCategory?: string[]
  knowledgeCorpora?: FlowSkillKnowledgeCorpus[]
  enabled?: boolean
  flowUpdatedBy?: FlowSkillUserSummary | null
  updatedAt: string
}

export const listSkills = async (clientId: number): Promise<FlowSkillListItem[]> => {
  return await knowledgeSourcesClient.get<FlowSkillListItem[]>({
    path: `${paths.skills}?${knowledgeSourcesQuery(clientId)}`
  })
}

export interface CopilotUploadedFile {
  id: string
  filename: string
  contentType: string
  gcsUri: string
  fileSizeBytes: number
  createdAt: string
  contentHash: string | null
  deduplicated: boolean
}

/**
 * Lists the user's uploaded CSV files for this client, via the BFF proxy.
 * Used to resolve `gcsUri → filename` when restoring a CSV attachment from
 * chat history, since AG-UI/ADK `file_data` parts don't carry the filename.
 */
export const listCopilotCsvFiles = async (clientId: number): Promise<CopilotUploadedFile[]> => {
  const res = await fetch(`/api/copilot/files?clientId=${encodeURIComponent(clientId)}&contentType=text/csv`)
  if (!res.ok) throw await apiClientErrorFromResponse(res, 'Failed to fetch copilot csv files')
  const json: { data: CopilotUploadedFile[] } = await res.json()
  return json.data
}

/** Uploads a single file to the user's Flow file library for this client, via the BFF proxy. */
export const uploadCopilotFile = async (clientId: number, file: File): Promise<CopilotUploadedFile> => {
  const form = new FormData()
  form.append('file', file, file.name)

  const res = await fetch(`/api/copilot/files?clientId=${encodeURIComponent(clientId)}`, {
    method: 'POST',
    body: form
  })

  if (!res.ok) {
    throw await apiClientErrorFromResponse(res, 'Failed to upload file')
  }

  return await res.json()
}

/** Max URIs Flow will sign in one call (`MAX_PREVIEW_URLS_PER_REQUEST`). */
export const MAX_PREVIEW_URLS_PER_REQUEST = 20

export interface CopilotPreviewUrls {
  /** Signed HTTPS URL by `gs://` URI. */
  urls: Record<string, string>
  /**
   * Original filename by `gs://` URI. Only library files have a stored record
   * to read the name from, so an entry can legitimately be missing.
   */
  filenames: Record<string, string>
}

/**
 * Signs preview URLs for GCS-backed attachments, via the BFF proxy.
 *
 * Chat history stores a `gs://` URI, which a browser cannot load, so every
 * image thumbnail needs a short-lived signed HTTPS URL. Flow omits URIs the
 * caller doesn't own, so the returned map may be smaller than the input.
 *
 * The original filenames come back on the same call: an AG-UI image part
 * carries only the URI, and Flow reads the file's record here anyway to check
 * ownership. `filenames` defaults to empty so a UI deployed ahead of Flow
 * degrades to a generic label instead of breaking.
 */
export const signCopilotPreviewUrls = async (clientId: number, gcsUris: string[]): Promise<CopilotPreviewUrls> => {
  if (gcsUris.length === 0) return { urls: {}, filenames: {} }

  const res = await fetch(`/api/copilot/files/preview-urls?clientId=${encodeURIComponent(clientId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gcsUris })
  })

  if (!res.ok) throw await apiClientErrorFromResponse(res, 'Failed to sign copilot preview urls')

  const json: { data: { urls?: Record<string, string>; filenames?: Record<string, string> } } = await res.json()
  return { urls: json.data?.urls ?? {}, filenames: json.data?.filenames ?? {} }
}
